/**
 * 座標変換。
 *
 * 原典 GoalView の `calcUTM` / `ll2xy` / `lom`（`GoalView.cpp:2250-2333`）の移植。
 * 実データ（佐久の map_saku.txt / TV の $_TVMARK）で検証済み（docs/01 §11, docs/03 §2）。
 */

import type { UtmPoint } from './types.ts';

// WGS84
const A = 6378137.0;
/** 原典に合わせた扁平率。厳密な WGS84 は 1/298.257223563 だが、
 *  既存データとの座標一致を優先してこの値を使う */
const F = 1 / 298.257223;
const E2 = 2 * F - F * F;
const K0 = 0.9996;
const FALSE_EASTING = 500000;

const rad = (deg: number) => (deg * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** 赤道から緯度 b までの子午線弧長 */
function meridianArc(b: number): number {
  const e4 = E2 * E2;
  const e6 = e4 * E2;
  const e8 = e6 * E2;
  const c = 1 - E2;
  const cad = A * c * (1 + (3 / 4) * E2 + (45 / 64) * e4 + (175 / 256) * e6 + (11025 / 16384) * e8);
  const cbd = A * c * ((3 / 4) * E2 + (15 / 16) * e4 + (525 / 512) * e6 + (2205 / 2048) * e8);
  const ccd = A * c * ((15 / 64) * e4 + (105 / 256) * e6 + (2205 / 4096) * e8);
  const cdd = A * c * ((35 / 512) * e6 + (315 / 2048) * e8);
  const ced = A * c * ((315 / 16384) * e8);
  return (
    cad * b -
    (cbd / 2) * Math.sin(2 * b) +
    (ccd / 4) * Math.sin(4 * b) -
    (cdd / 6) * Math.sin(6 * b) +
    (ced / 8) * Math.sin(8 * b)
  );
}

/** Gauss-Krüger 展開 */
function llToXy(b: number, dl: number): { x: number; y: number } {
  const dl2 = dl * dl;
  const dl4 = dl2 * dl2;
  const sb = Math.sin(b);
  const cb = Math.cos(b);
  const cb3 = cb * cb * cb;
  const cb5 = cb3 * cb * cb;
  const tb2 = (sb * sb) / cb / cb;
  const tb4 = tb2 * tb2;
  const ed2 = E2 / (1 - E2);
  const eta2 = ed2 * cb * cb;
  const rn = A / (1 - F) / Math.sqrt(1 + eta2);

  const x =
    rn * cb * dl +
    (rn / 6) * cb3 * (1 - tb2 + eta2) * dl * dl2 +
    (rn / 120) * cb5 * (5 - 18 * tb2 + tb4) * dl * dl4;
  const y =
    meridianArc(b) +
    (rn / 2) * sb * cb * dl2 +
    (rn / 24) * sb * cb3 * (5 - tb2 + 9 * eta2 + 4 * eta2 * eta2) * dl4 +
    (rn / 720) * sb * cb5 * (61 - 58 * tb2 + tb4) * dl4 * dl2;
  return { x, y };
}

/** 経度から UTM ゾーン番号を求める */
export function utmZoneOf(lon: number): number {
  return (Math.floor((lon + 180) / 6) % 60) + 1;
}

/** 緯度経度 → UTM。北緯東経を前提とする（原典と同じ制約） */
export function toUtm(lon: number, lat: number): UtmPoint {
  const zone = utmZoneOf(lon);
  const lon0 = zone * 6 - 183;
  const { x, y } = llToXy(rad(lat), rad(lon - lon0));
  // 原典に合わせてミリメートル単位で切り捨てる
  return {
    easting: Math.floor((x * K0 + FALSE_EASTING) * 1000) / 1000,
    northing: Math.floor(y * K0 * 1000) / 1000,
    zone,
  };
}

/** UTM → 緯度経度（逆変換） */
export function fromUtm(p: UtmPoint): { lat: number; lon: number } {
  const x = p.easting - FALSE_EASTING;
  const m = p.northing / K0;
  const mu = m / (A * (1 - E2 / 4 - (3 * E2 ** 2) / 64 - (5 * E2 ** 3) / 256));
  const e1 = (1 - Math.sqrt(1 - E2)) / (1 + Math.sqrt(1 - E2));
  const phi =
    mu +
    ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu) +
    ((21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu) +
    ((151 * e1 ** 3) / 96) * Math.sin(6 * mu) +
    ((1097 * e1 ** 4) / 512) * Math.sin(8 * mu);

  const ep2 = E2 / (1 - E2);
  const c = ep2 * Math.cos(phi) ** 2;
  const t = Math.tan(phi) ** 2;
  const n = A / Math.sqrt(1 - E2 * Math.sin(phi) ** 2);
  const r = (A * (1 - E2)) / (1 - E2 * Math.sin(phi) ** 2) ** 1.5;
  const d = x / (n * K0);

  const lat =
    phi -
    ((n * Math.tan(phi)) / r) *
      ((d * d) / 2 -
        ((5 + 3 * t + 10 * c - 4 * c * c - 9 * ep2) * d ** 4) / 24 +
        ((61 + 90 * t + 298 * c + 45 * t * t - 252 * ep2 - 3 * c * c) * d ** 6) / 720);
  const lon =
    (d -
      ((1 + 2 * t + c) * d ** 3) / 6 +
      ((5 - 2 * c + 28 * t - 3 * c * c + 8 * ep2 + 24 * t * t) * d ** 5) / 120) /
    Math.cos(phi);

  return { lat: deg(lat), lon: p.zone * 6 - 183 + deg(lon) };
}

/**
 * 8桁UTM表記を組み立てる。
 *
 * 前4桁 = 東距の100km区画内の値 ÷ 10、後4桁 = 北距の同。
 * 原典 `GoalView.cpp:1621-1650` の実装であり、TV の $_TVMARK と突き合わせて
 * 実データ7組すべてで一致を確認済み（docs/03 §2）。
 */
export function toEightDigit(easting: number, northing: number): string {
  const e = Math.floor((easting % 100000) / 10);
  const n = Math.floor((northing % 100000) / 10);
  return `${String(e).padStart(4, '0')}${String(n).padStart(4, '0')}`;
}

/**
 * 8桁UTM表記を座標に戻す。
 *
 * 100km 区画の情報を持たないため、**基準点が必須**。
 * 原典は地図キャリブレーションから区画（`g_utm_band_x/y`）を得ている。
 *
 * @param code 8桁の数字文字列
 * @param band 100km 区画の基準（原点側）。`bandOf()` で作れる
 */
export function fromEightDigit(
  code: string,
  band: { eastingBase: number; northingBase: number; zone: number },
): UtmPoint | null {
  if (!/^\d{8}$/.test(code)) return null;
  return {
    easting: Number(code.slice(0, 4)) * 10 + band.eastingBase,
    northing: Number(code.slice(4, 8)) * 10 + band.northingBase,
    zone: band.zone,
  };
}

/** ある地点が属する 100km 区画の基準値を求める */
export function bandOf(p: UtmPoint): { eastingBase: number; northingBase: number; zone: number } {
  return {
    eastingBase: Math.floor(p.easting / 100000) * 100000,
    northingBase: Math.floor(p.northing / 100000) * 100000,
    zone: p.zone,
  };
}

/**
 * グリッド表記を組み立てる（東距・北距をそれぞれ独立に4桁/5桁で選べる）。
 *
 * 5桁側は1000km区画（100万m）基準にして1桁増やす。UTMゾーンの境目に
 * 近い会場で区画の曖昧さを減らすための拡張表記（原典GoalViewには無い、
 * 大会側の運用に合わせたGV4D独自の拡張）。`wideEasting=wideNorthing=false`
 * なら`toEightDigit`と完全に一致する。
 */
export function toGridCode(
  easting: number,
  northing: number,
  wideEasting: boolean,
  wideNorthing: boolean,
): string {
  const e = Math.floor((easting % (wideEasting ? 1_000_000 : 100000)) / 10);
  const n = Math.floor((northing % (wideNorthing ? 1_000_000 : 100000)) / 10);
  return `${String(e).padStart(wideEasting ? 5 : 4, '0')}${String(n).padStart(wideNorthing ? 5 : 4, '0')}`;
}

/**
 * グリッド表記を座標に戻す。
 *
 * 区画の情報を持たないため、**基準点が必須**。`gridBandOf()` で作れる。
 *
 * @param code 数字文字列（桁数は wideEasting/wideNorthing で決まる）
 * @param band 区画の基準。`gridBandOf()` で作れる
 */
export function fromGridCode(
  code: string,
  wideEasting: boolean,
  wideNorthing: boolean,
  band: { eastingBase: number; northingBase: number; zone: number },
): UtmPoint | null {
  const eDigits = wideEasting ? 5 : 4;
  const nDigits = wideNorthing ? 5 : 4;
  if (!new RegExp(`^\\d{${eDigits + nDigits}}$`).test(code)) return null;
  return {
    easting: Number(code.slice(0, eDigits)) * 10 + band.eastingBase,
    northing: Number(code.slice(eDigits)) * 10 + band.northingBase,
    zone: band.zone,
  };
}

/** ある地点が属する区画の基準値（東距・北距それぞれ4桁なら100km、5桁なら1000km区画） */
export function gridBandOf(
  p: UtmPoint,
  wideEasting: boolean,
  wideNorthing: boolean,
): { eastingBase: number; northingBase: number; zone: number } {
  const eStep = wideEasting ? 1_000_000 : 100000;
  const nStep = wideNorthing ? 1_000_000 : 100000;
  return {
    eastingBase: Math.floor(p.easting / eStep) * eStep,
    northingBase: Math.floor(p.northing / nStep) * nStep,
    zone: p.zone,
  };
}

/** 2地点間の距離 [m]（UTM 平面上。同一ゾーン前提） */
export function utmDistance(a: UtmPoint, b: UtmPoint): number {
  return Math.hypot(a.easting - b.easting, a.northing - b.northing);
}

// ---------------------------------------------------------------------------
// 競技地図オーバーレイ用のアフィン変換
// ---------------------------------------------------------------------------

/** UTM → 画像ピクセルのアフィン変換パラメータ */
export interface AffineParams {
  /** px = a·E + b·N + c */
  a: number;
  b: number;
  c: number;
  /** py = d·E + e·N + f */
  d: number;
  e: number;
  f: number;
}

/**
 * 校正点3組からアフィン変換を求める。
 *
 * 既存の `map.txt` は最大6点（3点×2ゾーン）を持つ（`GoalViewF2.cpp:528`）。
 * 佐久のデータで検証済み: 106.57 dot/km / 回転 0.22°（docs/01 §11）。
 */
export function solveAffine(
  points: Array<{ utm: UtmPoint; px: number; py: number }>,
): AffineParams | null {
  if (points.length < 3) return null;
  const m = points.slice(0, 3).map((p) => [p.utm.easting, p.utm.northing, 1]);
  const x = solve3(m, [points[0].px, points[1].px, points[2].px]);
  const y = solve3(m, [points[0].py, points[1].py, points[2].py]);
  if (!x || !y) return null;
  return { a: x[0], b: x[1], c: x[2], d: y[0], e: y[1], f: y[2] };
}

/** 3元1次連立方程式をガウスの消去法（ピボット選択つき）で解く */
function solve3(a: number[][], rhs: number[]): number[] | null {
  const m = a.map((row, i) => [...row, rhs[i]]);
  const n = 3;
  for (let k = 0; k < n; k++) {
    let pivot = k;
    for (let i = k; i < n; i++) if (Math.abs(m[i][k]) > Math.abs(m[pivot][k])) pivot = i;
    if (Math.abs(m[pivot][k]) < 1e-12) return null;
    [m[k], m[pivot]] = [m[pivot], m[k]];
    for (let i = k + 1; i < n; i++) {
      const r = m[i][k] / m[k][k];
      for (let j = k; j <= n; j++) m[i][j] -= r * m[k][j];
    }
  }
  const out = [0, 0, 0];
  for (let i = n - 1; i >= 0; i--) {
    let s = m[i][n];
    for (let j = i + 1; j < n; j++) s -= m[i][j] * out[j];
    out[i] = s / m[i][i];
  }
  return out;
}

/** UTM 座標を画像ピクセルへ変換する */
export function applyAffine(p: AffineParams, utm: UtmPoint): { px: number; py: number } {
  return {
    px: p.a * utm.easting + p.b * utm.northing + p.c,
    py: p.d * utm.easting + p.e * utm.northing + p.f,
  };
}

/**
 * アフィン変換の逆変換（画像ピクセル → UTM）。`applyAffine` の逆。
 *
 * 競技地図タイル生成で「元画像のどの範囲を変換すればよいか」を求めるのに使う
 * （タイル座標→経緯度→`toMapPixel`で元画像を引く向きとは逆に、
 * 元画像の四隅がどの経緯度に当たるかを知りたい）。
 * 行列が縮退している（校正点が一直線に並んでいる等）場合は null。
 */
export function unapplyAffine(
  p: AffineParams,
  px: number,
  py: number,
): { easting: number; northing: number } | null {
  const det = p.a * p.e - p.b * p.d;
  if (Math.abs(det) < 1e-12) return null;
  const dx = px - p.c;
  const dy = py - p.f;
  return {
    easting: (p.e * dx - p.b * dy) / det,
    northing: (p.a * dy - p.d * dx) / det,
  };
}

/** アフィン変換のスケール [dot/km] と回転 [deg] を求める（校正の妥当性確認用） */
export function affineScale(p: AffineParams): { scaleX: number; scaleY: number; rotation: number } {
  return {
    scaleX: Math.hypot(p.a, p.d) * 1000,
    scaleY: Math.hypot(p.b, p.e) * 1000,
    rotation: deg(Math.atan2(p.b, p.a)),
  };
}
